import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Copy, Check, Key, ExternalLink } from 'lucide-react'
import { SEOHead } from '@/components/SEOHead'

const getApiBaseUrl = () => {
  if (typeof window === 'undefined') return (import.meta as any).env?.VITE_API_URL || 'http://localhost:8000'
  if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return `${window.location.protocol}//${window.location.hostname}/api`
  }
  return (import.meta as any).env?.VITE_API_URL || 'http://localhost:8000'
}

const API_BASE = getApiBaseUrl()

// ── Types ─────────────────────────────────────────────────────────────────────
interface Param { name: string; type: string; required?: boolean; description: string; example?: string }

interface Endpoint {
  method: 'GET' | 'POST' | 'DELETE' | 'PUT'
  path: string
  summary: string
  description: string
  cost?: number
  plan: string
  pathParams?: Param[]
  queryParams?: Param[]
  bodyParams?: Param[]
  requestExample?: Record<string, unknown>
  responseExample: Record<string, unknown>
  notes?: string
  errors?: Record<string, string>
}

interface Section {
  id: string
  title: string
  plan: string
  description: string
  basePrefix: string
  endpoints: Endpoint[]
}

// ── Data ──────────────────────────────────────────────────────────────────────
const SECTIONS: Section[] = [
  {
    id: 'breachint',
    title: 'BreachINT',
    plan: 'STARTER+',
    description: 'Breach intelligence search across emails, usernames, phones, domains, and IPs. Auto-enriched with stealer logs, WHOIS, Gmail OSINT, and LeakIX data.',
    basePrefix: '/v1/breach',
    endpoints: [
      {
        method: 'POST', path: '/v1/breach/search', summary: 'Full breach graph search', cost: 3, plan: 'STARTER+',
        description: 'Search the breach intelligence database. Returns a graph of nodes (email, password, breach source, IP, username) and edges connecting them. Auto-enriched with Hudson Rock stealer logs, LeakIX, and Gmail OSINT for @gmail.com addresses. Results are cached — repeat queries return cached data at no extra cost.',
        bodyParams: [
          { name: 'query', type: 'string', required: true, description: 'Email, username, phone number, domain, or IP address', example: 'user@example.com' },
          { name: 'force_refresh', type: 'boolean', required: false, description: 'Bypass cache and re-fetch live data', example: 'false' },
        ],
        requestExample: { query: 'user@example.com', force_refresh: false },
        responseExample: {
          nodes: [
            { id: 'email:user@example.com', type: 'email', label: 'user@example.com', props: {} },
            { id: 'source:linkedin-2012', type: 'source', label: 'LinkedIn 2012', props: { records_affected: 165000000, data_types: 'Email, Password' } }
          ],
          edges: [{ source: 'email:user@example.com', target: 'source:linkedin-2012', relation: 'found_in', weight: 1.0 }],
          facets: { sources: ['sycek_intelligence', 'hudson_rock'] },
          _credits: { credits_used: 3, credits_remaining: 47, credits_total: 50 }
        },
        errors: { '400': 'Missing query', '401': 'Invalid API key', '402': 'Insufficient credits', '403': 'Plan does not include BreachINT', '500': 'Search failed' }
      },
      {
        method: 'GET', path: '/v1/breach/whois/{domain}', summary: 'WHOIS lookup', cost: 1, plan: 'STARTER+',
        description: 'Fetch WHOIS registration data for a domain: registrant info, registrar, nameservers, creation/expiry dates, and status flags.',
        pathParams: [{ name: 'domain', type: 'string', required: true, description: 'Domain name to look up', example: 'example.com' }],
        responseExample: {
          domain: 'example.com', registrar: 'IANA',
          registrant: { name: 'IANA', email: 'res-dom@iana.org', country: 'US' },
          created: '1995-08-14', expires: '2027-08-13',
          nameservers: ['A.IANA-SERVERS.NET', 'B.IANA-SERVERS.NET'],
          status: ['clientDeleteProhibited'],
          _credits: { credits_used: 1, credits_remaining: 46, credits_total: 50 }
        },
        errors: { '401': 'Invalid API key', '402': 'Insufficient credits', '403': 'Plan does not include BreachINT', '500': 'WHOIS lookup failed' }
      },
      {
        method: 'GET', path: '/v1/breach/reverse-whois', summary: 'Reverse WHOIS', cost: 2, plan: 'STARTER+',
        description: 'Find all domains registered by a given registrant field value. Useful for pivoting from an email or company name to all domains they own.',
        queryParams: [
          { name: 'search_term', type: 'string', required: true, description: 'Value to search for', example: 'admin@example.com' },
          { name: 'search_field', type: 'string', required: true, description: 'Field to search. One of: domain_keyword, domain_name, registrant_email, registrant_name, registrant_company, registrant_phone, registrar_name, name_servers', example: 'registrant_email' },
          { name: 'database', type: 'string', required: false, description: '"current" (active registrations) or "historic"', example: 'current' },
          { name: 'page_size', type: 'integer', required: false, description: 'Results per page (default 10)', example: '10' },
        ],
        responseExample: {
          domains: ['example.com', 'example.net', 'example.org'],
          total: 3, search_field: 'registrant_email', search_term: 'admin@example.com',
          _credits: { credits_used: 2, credits_remaining: 44, credits_total: 50 }
        },
        errors: { '400': 'Missing search_term or invalid search_field', '401': 'Invalid API key', '402': 'Insufficient credits', '403': 'Plan does not include BreachINT' }
      },
      {
        method: 'GET', path: '/v1/breach/caller-id', summary: 'Caller ID lookup', cost: 2, plan: 'STARTER+',
        description: 'Look up a phone number to identify the registered name and linked social accounts using the Eyecon Caller ID database.',
        queryParams: [
          { name: 'country_code', type: 'string', required: true, description: 'Country dialing code without +', example: '1' },
          { name: 'number', type: 'string', required: true, description: 'Phone number without country code', example: '5551234567' },
        ],
        responseExample: {
          name: 'John Doe', phone: '+15551234567',
          facebook_profile: 'https://facebook.com/profile/...', carrier: 'Verizon', line_type: 'mobile',
          _credits: { credits_used: 2, credits_remaining: 42, credits_total: 50 }
        },
        errors: { '400': 'Missing country_code or number', '401': 'Invalid API key', '402': 'Insufficient credits', '403': 'Plan does not include BreachINT' }
      },
      {
        method: 'GET', path: '/v1/breach/gmail-osint/{email}', summary: 'Gmail OSINT', cost: 2, plan: 'STARTER+',
        description: 'Retrieve Gmail profile data for a @gmail.com address: display name, profile picture URL, and account recovery hints. Only works with @gmail.com addresses.',
        pathParams: [{ name: 'email', type: 'string', required: true, description: 'Gmail address to look up', example: 'target@gmail.com' }],
        responseExample: {
          email: 'target@gmail.com', name: 'Target User',
          profile_photo: 'https://lh3.googleusercontent.com/...',
          recovery_email_hint: 't****@outlook.com', recovery_phone_hint: '+1 *** *** **67',
          _credits: { credits_used: 2, credits_remaining: 40, credits_total: 50 }
        },
        notes: 'Only works with @gmail.com addresses.',
        errors: { '400': 'Not a @gmail.com address', '401': 'Invalid API key', '402': 'Insufficient credits', '403': 'Plan does not include BreachINT' }
      }
    ]
  },
  {
    id: 'social',
    title: 'Social OSINT',
    plan: 'PRO+',
    description: 'Multi-platform social media intelligence across Twitter, Reddit, YouTube, Instagram, TikTok, Telegram, and Facebook. Includes NWS scoring, CIB detection, IOC extraction, and STIX 2.1 export.',
    basePrefix: '/v1/social',
    endpoints: [
      {
        method: 'POST', path: '/v1/social/search', summary: 'Multi-platform search', cost: 2, plan: 'PRO+',
        description: 'Search across multiple social platforms simultaneously and return posts with sentiment, hashtags, engagement metrics, and platform metadata.',
        bodyParams: [
          { name: 'query', type: 'string', required: true, description: 'Search query or hashtag', example: 'government protest ireland' },
          { name: 'platforms', type: 'string[]', required: true, description: 'Platforms: twitter, reddit, youtube, instagram, tiktok, telegram, facebook', example: '["twitter","reddit"]' },
          { name: 'count_per_platform', type: 'integer', required: false, description: 'Posts per platform (default 50)', example: '50' },
        ],
        requestExample: { query: 'government protest ireland', platforms: ['twitter', 'reddit', 'youtube'], count_per_platform: 50 },
        responseExample: {
          posts: [{ id: '1', platform: 'twitter', author: 'user1', text: 'Sample post...', timestamp: '2026-04-13T09:00:00Z', likes: 120, shares: 45, hashtags: ['#ireland'] }],
          total_posts: 143, platforms_searched: ['twitter', 'reddit', 'youtube'],
          _credits: { credits_used: 2, credits_remaining: 98, credits_total: 100 }
        },
        errors: { '400': 'Missing query or platforms', '401': 'Invalid API key', '402': 'Insufficient credits', '403': 'PRO+ plan required' }
      },
      {
        method: 'POST', path: '/v1/social/analyze', summary: 'Narrative analysis', cost: 3, plan: 'PRO+',
        description: 'Analyze a post batch for hashtag co-occurrence networks, top influencers, narrative clusters, UTC timeline distribution, and NWS (Narrative Weaponization Score) per cluster. NWS range: critical ≥75, high ≥50, medium ≥25, low <25.',
        bodyParams: [
          { name: 'posts', type: 'Post[]', required: true, description: 'Array of posts from /v1/social/search' },
        ],
        requestExample: { posts: [{ id: '1', author: 'user1', text: 'Protest update...', hashtags: ['#ireland'], platform: 'twitter', timestamp: '2026-04-13T09:00:00Z', likes: 100, shares: 40, comments: 10 }] },
        responseExample: {
          hashtag_network: { nodes: [{ id: 'ireland', count: 87 }], edges: [{ source: 'ireland', target: 'protest', weight: 42 }] },
          top_influencers: [{ author: 'user1', total_engagement: 1500, post_count: 12 }],
          narrative_clusters: [{ terms: ['government', 'ireland'], post_count: 43, nws: 82, nws_level: 'critical', nws_breakdown: { velocity: 30, neg_conc: 17, platform: 10, amplification: 25 } }],
          timeline: { '09': 15, '10': 32, '11': 28 },
          _credits: { credits_used: 3, credits_remaining: 95, credits_total: 100 }
        },
        errors: { '400': 'Missing or empty posts array', '401': 'Invalid API key', '402': 'Insufficient credits', '403': 'PRO+ plan required' }
      },
      {
        method: 'POST', path: '/v1/social/intelligence', summary: 'Full ML intelligence pass', cost: 5, plan: 'PRO+',
        description: 'Full ML intelligence pass: per-post deep sentiment via distilRoBERTa, Coordinated Inauthentic Behavior (CIB) detection, IOC extraction, geospatial inference, and velocity/surge analysis. Expect 10–30s latency for large batches.',
        bodyParams: [
          { name: 'posts', type: 'Post[]', required: true, description: 'Array of posts from /v1/social/search' },
          { name: 'keyword', type: 'string', required: true, description: 'Topic keyword for context', example: 'ireland protest' },
        ],
        requestExample: { posts: [{ id: '1', author: 'user1', text: 'Pray for Ireland the government must fall', platform: 'twitter', timestamp: '2026-04-13T09:00:00Z', likes: 100, shares: 50, comments: 10 }], keyword: 'ireland protest' },
        responseExample: {
          keyword: 'ireland protest', post_count: 1,
          cib: { cib_score: 72, coordination_flag: true, summary: '2 similar-text clusters detected' },
          iocs: { urls: [{ value: 'https://t.co/abc', count: 3 }], handles: [{ value: '@rebel_voice', count: 5 }], total_iocs: 2 },
          geo: { inference_method: 'spacy_ner', top_country: 'Ireland', location_clusters: ['Cork', 'Dublin'] },
          velocity: { surge: true, z_score: 94.67, alert_level: 'high' },
          _credits: { credits_used: 5, credits_remaining: 90, credits_total: 100 }
        },
        notes: 'Runs distilRoBERTa ML model. Expect 10–30s latency for large batches.',
        errors: { '400': 'Missing posts or keyword', '401': 'Invalid API key', '402': 'Insufficient credits', '403': 'PRO+ plan required', '503': 'ML model not ready — retry in a few seconds' }
      },
      {
        method: 'POST', path: '/v1/social/actor-profile', summary: 'Behavioral actor profile', cost: 3, plan: 'PRO+',
        description: 'Build a behavioral profile for a specific author: platform breakdown, peak activity hours, posting cadence coefficient of variation, engagement totals, and bot-behavior flags.',
        bodyParams: [
          { name: 'author_id', type: 'string', required: true, description: 'Author identifier to profile', example: 'user1' },
          { name: 'posts', type: 'Post[]', required: true, description: 'All posts (filtered to author_id server-side)' },
        ],
        requestExample: { author_id: 'user1', posts: [{ id: '1', author: 'user1', author_id: 'user1', text: '...', platform: 'twitter', timestamp: '2026-04-13T09:00:00Z', likes: 100, shares: 50, comments: 10 }] },
        responseExample: {
          author_id: 'user1', author: 'user1', post_count: 18,
          platforms: { twitter: 15, youtube: 3 }, total_engagement: 3200,
          peak_hours: [9, 10, 14], cadence_cv: 0.08, cadence_flag: true,
          followers: 1240, verified: false,
          _credits: { credits_used: 3, credits_remaining: 87, credits_total: 100 }
        },
        notes: 'cadence_flag: true means posting interval is suspiciously regular (CV < 0.15) — a bot-behavior indicator.',
        errors: { '400': 'Missing author_id or posts', '401': 'Invalid API key', '402': 'Insufficient credits', '403': 'PRO+ plan required' }
      },
      {
        method: 'POST', path: '/v1/social/stix-export', summary: 'STIX 2.1 bundle export', cost: 2, plan: 'PRO+',
        description: 'Generate a STIX 2.1 bundle from posts, IOCs, and narrative clusters. Returns identity, indicators, threat-actors, notes, and a report object. Compatible with any SIEM that ingests STIX 2.1.',
        bodyParams: [
          { name: 'query', type: 'string', required: true, description: 'Investigation topic', example: 'ireland protest' },
          { name: 'posts', type: 'Post[]', required: true, description: 'Posts array' },
          { name: 'iocs', type: 'object', required: false, description: 'IOC data from /v1/social/intelligence' },
          { name: 'clusters', type: 'object[]', required: false, description: 'Narrative clusters from /v1/social/analyze' },
        ],
        requestExample: { query: 'ireland protest', posts: [{ id: '1', author: 'user1', text: '...' }], iocs: { urls: [{ value: 'https://t.co/abc123', count: 2 }] }, clusters: [{ terms: ['government'], nws: 82, nws_level: 'critical' }] },
        responseExample: {
          type: 'bundle', spec_version: '2.1',
          objects: [
            { type: 'identity', name: 'Sycek OSINT Platform', spec_version: '2.1' },
            { type: 'indicator', name: 'https://t.co/abc123', spec_version: '2.1' },
            { type: 'threat-actor', name: 'user1', spec_version: '2.1' },
            { type: 'report', name: 'SOCINT Report: ireland protest', spec_version: '2.1' }
          ],
          _credits: { credits_used: 2, credits_remaining: 85, credits_total: 100 }
        },
        errors: { '400': 'Missing query or posts', '401': 'Invalid API key', '402': 'Insufficient credits', '403': 'PRO+ plan required' }
      },
      {
        method: 'GET', path: '/v1/social/stream', summary: 'Live stream events', cost: 1, plan: 'PRO+',
        description: 'Fetch the latest posts from all active social monitoring streams. Returns events in reverse-chronological order.',
        queryParams: [
          { name: 'limit', type: 'integer', required: false, description: 'Number of events to return (1–500, default 50)', example: '50' },
        ],
        responseExample: {
          events: [{ id: 'evt1', platform: 'twitter', text: 'Breaking news...', author: 'user1', timestamp: '2026-04-13T10:00:00Z', likes: 50 }],
          total: 1, _credits: { credits_used: 1, credits_remaining: 84, credits_total: 100 }
        },
        errors: { '401': 'Invalid API key', '402': 'Insufficient credits', '403': 'PRO+ plan required' }
      },
    ]
  },
  {
    id: 'twitter',
    title: 'X Investigation',
    plan: 'ENTERPRISE',
    description: 'Deep Twitter/X account investigation: user profiles, tweet timelines, follower networks, advanced search, event-centric multi-phase analysis, and cached full investigations.',
    basePrefix: '/v1/twitter',
    endpoints: [
      {
        method: 'GET', path: '/v1/twitter/user', summary: 'User profile', cost: 2, plan: 'ENTERPRISE',
        description: 'Fetch profile metadata for a Twitter/X account: display name, bio, follower/following counts, tweet count, verified status, and account creation date.',
        queryParams: [
          { name: 'username', type: 'string', required: true, description: 'Twitter/X username (without @)', example: 'elonmusk' },
        ],
        responseExample: {
          success: true,
          data: { id: '44196397', username: 'elonmusk', name: 'Elon Musk', followers_count: 180000000, following_count: 640, tweet_count: 42000, verified: true, created_at: '2009-06-02T20:12:29Z', description: 'Mars & cars' },
          _credits: { credits_used: 2, credits_remaining: 198, credits_total: 200 }
        },
        errors: { '400': 'Missing username', '401': 'Invalid API key', '402': 'Insufficient credits', '403': 'ENTERPRISE plan required', '404': 'Account not found' }
      },
      {
        method: 'GET', path: '/v1/twitter/tweets', summary: 'Recent tweets', cost: 2, plan: 'ENTERPRISE',
        description: 'Fetch recent tweets for a user including text, hashtags, mentions, engagement metrics, and media attachments.',
        queryParams: [
          { name: 'username', type: 'string', required: true, description: 'Twitter/X username', example: 'elonmusk' },
          { name: 'count', type: 'integer', required: false, description: 'Number of tweets (1–100, default 20)', example: '20' },
        ],
        responseExample: {
          tweets: [{ id: '1234567890', text: 'Tweet content...', created_at: '2026-04-13T08:00:00Z', like_count: 15000, retweet_count: 3200, reply_count: 890, hashtags: ['#AI'], mentions: ['@openai'] }],
          total: 20, _credits: { credits_used: 2, credits_remaining: 196, credits_total: 200 }
        },
        errors: { '400': 'Missing username or invalid count', '401': 'Invalid API key', '402': 'Insufficient credits', '403': 'ENTERPRISE plan required' }
      },
      {
        method: 'GET', path: '/v1/twitter/search', summary: 'Advanced search', cost: 3, plan: 'ENTERPRISE',
        description: 'Advanced Twitter search using full operator syntax: from:user, to:user, #hashtag, lang:en, since:2026-01-01, -exclude, exact phrases, min_retweets:N.',
        queryParams: [
          { name: 'q', type: 'string', required: true, description: 'Search query with full Twitter operator syntax', example: 'from:elonmusk AI lang:en' },
          { name: 'search_type', type: 'string', required: false, description: 'Latest | Top | People | Media (default Latest)', example: 'Latest' },
          { name: 'limit', type: 'integer', required: false, description: 'Number of results (default 20)', example: '20' },
        ],
        responseExample: {
          tweets: [{ id: '111', text: 'AI is transforming...', author: 'elonmusk', created_at: '2026-04-10T12:00:00Z', likes: 8200 }],
          total: 20, query: 'from:elonmusk AI lang:en',
          _credits: { credits_used: 3, credits_remaining: 193, credits_total: 200 }
        },
        errors: { '400': 'Missing q parameter', '401': 'Invalid API key', '402': 'Insufficient credits', '403': 'ENTERPRISE plan required' }
      },
      {
        method: 'GET', path: '/v1/twitter/tweet/{tweet_id}/replies', summary: 'Tweet replies', cost: 2, plan: 'ENTERPRISE',
        description: 'Fetch replies to a specific tweet. Useful for mapping conversation trees and identifying amplification networks.',
        pathParams: [{ name: 'tweet_id', type: 'string', required: true, description: 'Tweet ID', example: '1234567890123456789' }],
        queryParams: [{ name: 'page_count', type: 'integer', required: false, description: 'Pages to fetch (1–5, ~20 replies/page)', example: '1' }],
        responseExample: {
          replies: [{ id: '222', text: 'Great point!', author: 'user2', likes: 45 }],
          total: 18, _credits: { credits_used: 2, credits_remaining: 191, credits_total: 200 }
        },
        errors: { '400': 'Missing tweet_id', '401': 'Invalid API key', '402': 'Insufficient credits', '403': 'ENTERPRISE plan required', '404': 'Tweet not found' }
      },
      {
        method: 'POST', path: '/v1/twitter/investigate', summary: 'Full account investigation', cost: 5, plan: 'ENTERPRISE',
        description: 'Comprehensive account investigation: profile, followers, following, and tweet timeline in a single call. Results are cached — repeat calls for the same username are served instantly at no extra cost (from_cache: true).',
        bodyParams: [
          { name: 'username', type: 'string', required: true, description: 'Twitter/X username to investigate', example: 'target_account' },
          { name: 'include_followers', type: 'boolean', required: false, description: 'Include followers list (default true)', example: 'true' },
          { name: 'include_followings', type: 'boolean', required: false, description: 'Include following list (default false)', example: 'false' },
          { name: 'include_tweets', type: 'boolean', required: false, description: 'Include recent tweets (default true)', example: 'true' },
          { name: 'max_followers', type: 'integer', required: false, description: 'Max followers to fetch (cap 500)', example: '200' },
          { name: 'max_tweets', type: 'integer', required: false, description: 'Max tweets to fetch (cap 100)', example: '20' },
          { name: 'force_refresh', type: 'boolean', required: false, description: 'Bypass cache', example: 'false' },
        ],
        requestExample: { username: 'target_account', include_followers: true, include_followings: false, include_tweets: true, max_followers: 200, max_tweets: 20, force_refresh: false },
        responseExample: {
          user_info: { success: true, data: { username: 'target_account', followers_count: 4200 } },
          followers: { followers: [{ username: 'follower1' }] },
          tweets: { tweets: [{ id: '1', text: 'Tweet...' }] },
          summary: { total_followers_fetched: 200, total_tweets_fetched: 20 },
          history_id: 'inv_abc123', from_cache: false,
          _credits: { credits_used: 5, credits_remaining: 186, credits_total: 200 }
        },
        errors: { '400': 'Missing username', '401': 'Invalid API key', '402': 'Insufficient credits', '403': 'ENTERPRISE plan required', '404': 'Account not found' }
      },
      {
        method: 'POST', path: '/v1/twitter/event', summary: 'Event investigation', cost: 5, plan: 'ENTERPRISE',
        description: 'Start an event-centric investigation from a tweet URL or ID. Phase 1 collects the seed tweet, all replies, quote tweets, retweets, and the full thread context.',
        bodyParams: [
          { name: 'post_id_or_url', type: 'string', required: true, description: 'Tweet URL or numeric tweet ID', example: 'https://x.com/user/status/1234567890123456789' },
          { name: 'max_reply_pages', type: 'integer', required: false, description: 'Pages of replies to fetch (default 3)', example: '3' },
          { name: 'max_quote_pages', type: 'integer', required: false, description: 'Pages of quote tweets (default 2)', example: '2' },
          { name: 'include_thread', type: 'boolean', required: false, description: 'Include full thread context (default true)', example: 'true' },
        ],
        requestExample: { post_id_or_url: 'https://x.com/user/status/1234567890123456789', max_reply_pages: 3, max_quote_pages: 2, include_thread: true },
        responseExample: {
          investigation_id: 'evt_xyz789', phase: 1,
          seed_tweet: { id: '1234567890', text: 'Original tweet...', author: 'user1', likes: 5000 },
          replies: [{ id: '2', text: 'Reply...', author: 'user2' }],
          quote_tweets: [{ id: '3', text: 'Quote...', author: 'user3' }],
          retweeters: [{ username: 'user4' }],
          thread: [{ id: '0', text: 'Thread parent...' }],
          stats: { total_replies: 142, total_quotes: 37, total_retweets: 820 },
          _credits: { credits_used: 5, credits_remaining: 181, credits_total: 200 }
        },
        notes: 'Use investigation_id to run subsequent phases (2–5: volume, sentiment/geo, attribution, synthesis) via the platform dashboard.',
        errors: { '400': 'Missing post_id_or_url', '401': 'Invalid API key', '402': 'Insufficient credits', '403': 'ENTERPRISE plan required', '404': 'Tweet not found or deleted' }
      }
    ]
  },
  {
    id: 'developer',
    title: 'Developer Portal',
    plan: 'Any plan',
    description: 'Manage API keys and view credit usage. These endpoints use your session JWT (Authorization: Bearer ...) not an X-API-Key. Access via sycek.io/app/developer.',
    basePrefix: '/developer',
    endpoints: [
      {
        method: 'POST', path: '/developer/keys', summary: 'Generate API key', cost: 0, plan: 'Any plan',
        description: 'Generate a new API key (format: sk_...). The full key is returned exactly once — store it securely. Maximum 5 keys per account.',
        notes: 'Use your JWT session token (Authorization: Bearer ...) for this endpoint, not an X-API-Key.',
        requestExample: {},
        responseExample: { key: 'sk_AbCdEfGhIjKlMnOpQrStUvWxYz1234567890AbCd', prefix: 'sk_AbCdEf', message: 'Save this key — it will not be shown again.', docs: 'Pass as X-API-Key header to /v1/* endpoints' }
      },
      {
        method: 'GET', path: '/developer/keys', summary: 'List API keys', cost: 0, plan: 'Any plan',
        description: 'List all API keys for the authenticated account. Keys are masked — only prefix and last 4 characters are shown.',
        responseExample: { keys: [{ masked: 'sk_AbCdEf...wxyz', prefix: 'sk_AbCdEf' }], count: 1, limit: 5 }
      },
      {
        method: 'DELETE', path: '/developer/keys/{prefix}', summary: 'Revoke API key', cost: 0, plan: 'Any plan',
        description: 'Revoke an API key permanently by its prefix. Get the prefix from GET /developer/keys.',
        pathParams: [{ name: 'prefix', type: 'string', required: true, description: 'Key prefix from the list endpoint', example: 'sk_AbCdEfGh' }],
        responseExample: { message: 'Key sk_AbCdEf...wxyz revoked.', revoked: true }
      },
      {
        method: 'GET', path: '/developer/usage', summary: 'Credit balance & usage', cost: 0, plan: 'Any plan',
        description: 'View your current credit balance, usage counters, subscription tier, and the credit cost for every public API endpoint.',
        responseExample: {
          credits_total: 200, credits_used: 14, credits_remaining: 186,
          unlimited: false, subscription_tier: 'starter',
          endpoint_costs: {
            social_osint: { 'POST /v1/social/search': 2, 'POST /v1/social/analyze': 3, 'POST /v1/social/intelligence': 5 },
            x_investigation: { 'GET /v1/twitter/user': 2, 'POST /v1/twitter/investigate': 5 },
            breachint: { 'POST /v1/breach/search': 3, 'GET /v1/breach/whois/{domain}': 1 }
          },
          api_keys_used: 1, api_keys_limit: 5
        }
      }
    ]
  }
]

const METHOD_STYLE: Record<string, string> = {
  GET: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/40',
  POST: 'bg-blue-500/15 text-blue-400 border border-blue-500/40',
  DELETE: 'bg-red-500/15 text-red-400 border border-red-500/40',
  PUT: 'bg-amber-500/15 text-amber-400 border border-amber-500/40',
}

const PLAN_STYLE: Record<string, string> = {
  'STARTER+': 'text-gray-300 bg-gray-700/50 border-gray-600/50',
  'PRO+': 'text-blue-300 bg-blue-900/30 border-blue-500/40',
  'ENTERPRISE': 'text-purple-300 bg-purple-900/30 border-purple-500/40',
  'Any plan': 'text-emerald-300 bg-emerald-900/20 border-emerald-500/30',
}

const SECTION_ACCENT: Record<string, string> = {
  breachint: 'border-red-500/30 text-red-400',
  social: 'border-blue-500/30 text-blue-400',
  twitter: 'border-sky-500/30 text-sky-400',
  developer: 'border-amber-500/30 text-amber-400',
}

const COST_STYLE = (cost: number) =>
  cost >= 5 ? 'bg-red-500/15 text-red-400 border border-red-500/40'
  : cost >= 3 ? 'bg-amber-500/15 text-amber-400 border border-amber-500/40'
  : cost > 0  ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/40'
  : 'bg-gray-700/40 text-gray-400 border border-gray-600/40'

// ── Sub-components ─────────────────────────────────────────────────────────────
function CopyButton({ text }: { text: string; id: string }) {
  const [copied, setCopied] = useState(false)
  const copy = () => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <button onClick={copy} className="p-1 rounded text-gray-500 hover:text-gray-300 transition-colors">
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  )
}

function CodeBlock({ code, id }: { code: string; id: string }) {
  return (
    <div className="relative bg-gray-950 border border-gray-800 rounded-lg overflow-hidden">
      <div className="absolute top-2 right-2">
        <CopyButton text={code} id={id} />
      </div>
      <pre className="p-4 pr-10 text-xs text-gray-300 overflow-x-auto font-mono leading-relaxed whitespace-pre-wrap">
        <code>{code}</code>
      </pre>
    </div>
  )
}

function ParamTable({ params, title, color }: { params: NonNullable<Endpoint['queryParams']>; title: string; color: string }) {
  return (
    <div>
      <h5 className={`text-xs font-semibold uppercase tracking-widest mb-2 ${color}`}>{title}</h5>
      <div className="border border-gray-800 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-900/60 border-b border-gray-800">
              <th className="text-left px-3 py-2 text-gray-500 font-medium w-36">Name</th>
              <th className="text-left px-3 py-2 text-gray-500 font-medium w-20">Type</th>
              <th className="text-left px-3 py-2 text-gray-500 font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            {params.map((p, i) => (
              <tr key={i} className="border-b border-gray-800/60 last:border-0">
                <td className="px-3 py-2 align-top">
                  <code className="text-amber-300 font-mono">{p.name}</code>
                  {p.required && <span className="ml-1 text-red-400 text-[10px]">*</span>}
                </td>
                <td className="px-3 py-2 align-top text-gray-500 font-mono">{p.type}</td>
                <td className="px-3 py-2 align-top text-gray-400 leading-relaxed">
                  {p.description}
                  {p.example && <span className="ml-1 text-gray-600">e.g. <code className="text-gray-400">{p.example}</code></span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function EndpointCard({ ep, sectionId, idx }: { ep: Endpoint; sectionId: string; idx: number }) {
  const anchor = `${sectionId}-${idx}`
  return (
    <div id={anchor} className="border border-gray-800 rounded-xl overflow-hidden mb-6 scroll-mt-20">
      {/* Header */}
      <div className="flex items-start gap-3 px-5 py-4 bg-gray-900/50 border-b border-gray-800 flex-wrap">
        <span className={`shrink-0 px-2 py-0.5 rounded text-xs font-bold font-mono tracking-wide ${METHOD_STYLE[ep.method]}`}>{ep.method}</span>
        <code className="text-gray-100 font-mono text-sm font-semibold flex-1 min-w-0 break-all">{ep.path}</code>
        <div className="flex items-center gap-2 shrink-0">
          {ep.cost !== undefined && (
            <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${COST_STYLE(ep.cost)}`}>
              {ep.cost > 0 ? `${ep.cost} credit${ep.cost !== 1 ? 's' : ''}` : 'Free'}
            </span>
          )}
          <span className={`px-2 py-0.5 rounded text-[11px] border font-medium ${PLAN_STYLE[ep.plan] || PLAN_STYLE['Any plan']}`}>{ep.plan}</span>
        </div>
      </div>

      <div className="px-5 py-5 space-y-5 bg-gray-900/20">
        {/* Summary + description */}
        <div>
          <h4 className="text-sm font-semibold text-gray-100 mb-1">{ep.summary}</h4>
          <p className="text-sm text-gray-400 leading-relaxed">{ep.description}</p>
        </div>

        {ep.notes && (
          <div className="flex items-start gap-2 bg-blue-950/40 border border-blue-800/40 rounded-lg px-4 py-3">
            <span className="text-blue-400 shrink-0 mt-0.5">ℹ</span>
            <p className="text-sm text-blue-300 leading-relaxed">{ep.notes}</p>
          </div>
        )}

        {/* Params */}
        {ep.pathParams && ep.pathParams.length > 0 && (
          <ParamTable params={ep.pathParams} title="Path Parameters" color="text-purple-400" />
        )}
        {ep.queryParams && ep.queryParams.length > 0 && (
          <ParamTable params={ep.queryParams} title="Query Parameters" color="text-cyan-400" />
        )}
        {ep.bodyParams && ep.bodyParams.length > 0 && (
          <ParamTable params={ep.bodyParams} title="Request Body" color="text-indigo-400" />
        )}

        {/* Request + Response */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {ep.requestExample && Object.keys(ep.requestExample).length > 0 && (
            <div>
              <h5 className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-2">Request Example</h5>
              <CodeBlock code={JSON.stringify(ep.requestExample, null, 2)} id={`req-${anchor}`} />
            </div>
          )}
          <div>
            <h5 className="text-xs font-semibold uppercase tracking-widest text-emerald-400 mb-2">Response Example</h5>
            <CodeBlock code={JSON.stringify(ep.responseExample, null, 2)} id={`res-${anchor}`} />
          </div>
        </div>

        {/* Errors */}
        {ep.errors && (
          <div>
            <h5 className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">Error Codes</h5>
            <div className="flex flex-wrap gap-2">
              {Object.entries(ep.errors).map(([code, msg]) => (
                <div key={code} className="flex items-center gap-1.5 bg-gray-900 border border-gray-800 rounded px-2 py-1">
                  <span className={`text-xs font-mono font-bold ${code.startsWith('4') ? 'text-red-400' : 'text-amber-400'}`}>{code}</span>
                  <span className="text-xs text-gray-400">{msg}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Quick-start code examples ──────────────────────────────────────────────────
const QS_CURL = (base: string) => `curl -X POST ${base}/v1/breach/search \\
  -H "X-API-Key: sk_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"query": "user@example.com"}'`

const QS_PYTHON = (base: string) => `import requests

r = requests.post(
    "${base}/v1/breach/search",
    headers={"X-API-Key": "sk_your_api_key_here"},
    json={"query": "user@example.com"}
)
data = r.json()
print(f"Credits used: {data['_credits']['credits_used']}")`

const QS_JS = (base: string) => `const res = await fetch('${base}/v1/breach/search', {
  method: 'POST',
  headers: {
    'X-API-Key': 'sk_your_api_key_here',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({ query: 'user@example.com' })
})
const data = await res.json()`

// ── Main component ─────────────────────────────────────────────────────────────
export default function APIDocs() {
  const [activeSection, setActiveSection] = useState('overview')
  const [qsLang, setQsLang] = useState<'curl' | 'python' | 'js'>('curl')

  useEffect(() => {
    const handler = () => {
      const sections = ['overview', 'auth', 'pricing', 'mcp', ...SECTIONS.map(s => s.id)]
      for (const id of [...sections].reverse()) {
        const el = document.getElementById(id)
        if (el && el.getBoundingClientRect().top <= 100) { setActiveSection(id); break }
      }
    }
    window.addEventListener('scroll', handler, { passive: true })
    return () => window.removeEventListener('scroll', handler)
  }, [])

  const qsCode = qsLang === 'curl' ? QS_CURL(API_BASE) : qsLang === 'python' ? QS_PYTHON(API_BASE) : QS_JS(API_BASE)

  const navItems = [
    { id: 'overview', label: 'Overview' },
    { id: 'auth', label: 'Authentication' },
    { id: 'pricing', label: 'Credit Pricing' },
    { id: 'mcp', label: 'MCP Integration' },
    ...SECTIONS.map(s => ({ id: s.id, label: s.title, plan: s.plan })),
  ]

  return (
    <>
      <SEOHead
        title="API Reference — Sycek OSINT"
        description="Developer reference for the Sycek OSINT Public API. BreachINT, Social OSINT, and X Investigation endpoints."
        keywords="OSINT API, breach intelligence API, social OSINT API, Twitter investigation API"
        canonical="/api-docs"
      />

      <div className="min-h-screen bg-gray-950 text-gray-100">
        {/* Top nav */}
        <header className="sticky top-0 z-50 border-b border-gray-800 bg-gray-950/95 backdrop-blur">
          <div className="max-w-screen-xl mx-auto px-6 h-14 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2.5 text-sm font-semibold text-gray-100 hover:text-white">
              <div className="w-7 h-7 bg-emerald-500 rounded flex items-center justify-center text-black font-bold text-sm">S</div>
              <span>Sycek OSINT</span>
              <span className="text-gray-600 font-normal">/</span>
              <span className="text-gray-400 font-normal">API Reference</span>
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/app/developer" className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-200 border border-gray-700 hover:border-gray-600 rounded px-3 py-1.5 transition-colors">
                <Key className="w-3.5 h-3.5" />
                Get API Key
              </Link>
              <Link to="/" className="text-xs text-gray-500 hover:text-gray-300 transition-colors">← Back to app</Link>
            </div>
          </div>
        </header>

        <div className="max-w-screen-xl mx-auto flex">
          {/* Left sidebar */}
          <nav className="hidden lg:block w-56 shrink-0 sticky top-14 h-[calc(100vh-3.5rem)] overflow-y-auto border-r border-gray-800 py-6 px-4">
            <div className="space-y-0.5">
              {navItems.map(item => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={() => setActiveSection(item.id)}
                  className={`flex items-center justify-between px-3 py-1.5 rounded text-sm transition-colors ${
                    activeSection === item.id
                      ? 'bg-gray-800 text-gray-100'
                      : 'text-gray-500 hover:text-gray-300 hover:bg-gray-900'
                  }`}
                >
                  <span>{item.label}</span>
                  {'plan' in item && item.plan && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${PLAN_STYLE[item.plan] || ''}`}>{item.plan}</span>
                  )}
                </a>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-gray-800">
              <div className="text-[11px] text-gray-600 font-medium uppercase tracking-wider mb-3">Resources</div>
              <div className="space-y-1">
                <Link to="/app/developer" className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-500 hover:text-gray-300 rounded hover:bg-gray-900 transition-colors">
                  <Key className="w-3.5 h-3.5" /> Developer Portal
                </Link>
                <a href="https://pypi.org/project/sycek-osint-mcp" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-500 hover:text-gray-300 rounded hover:bg-gray-900 transition-colors">
                  <ExternalLink className="w-3.5 h-3.5" /> MCP on PyPI
                </a>
              </div>
            </div>
          </nav>

          {/* Main content */}
          <main className="flex-1 min-w-0 py-10 px-6 lg:px-10 max-w-4xl">

            {/* Overview */}
            <section id="overview" className="mb-14 scroll-mt-20">
              <div className="mb-1">
                <span className="text-xs text-emerald-400 font-semibold uppercase tracking-widest">Sycek OSINT</span>
              </div>
              <h1 className="text-3xl font-bold text-white mb-3">API Reference</h1>
              <p className="text-gray-400 text-base leading-relaxed mb-6">
                The Sycek Public API provides programmatic access to 17 OSINT intelligence endpoints across three modules — BreachINT, Social OSINT, and X Investigation. All requests are authenticated via an <code className="text-amber-300 bg-gray-900 px-1.5 py-0.5 rounded text-sm">X-API-Key</code> header and billed per call against your credit balance.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Base URL', value: 'sycek.io/api', sub: 'HTTPS only' },
                  { label: 'Endpoints', value: '17', sub: '3 modules' },
                  { label: 'Auth', value: 'X-API-Key', sub: 'sk_ prefix' },
                  { label: 'Billing', value: 'Per-call', sub: 'Credit-based' },
                ].map(item => (
                  <div key={item.label} className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                    <div className="text-xs text-gray-500 mb-1">{item.label}</div>
                    <div className="text-sm font-semibold text-gray-100">{item.value}</div>
                    <div className="text-xs text-gray-600">{item.sub}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* Authentication */}
            <section id="auth" className="mb-14 scroll-mt-20">
              <h2 className="text-xl font-bold text-white mb-4 pb-2 border-b border-gray-800">Authentication</h2>
              <p className="text-gray-400 text-sm leading-relaxed mb-4">
                Include your API key as the <code className="text-amber-300 bg-gray-900 px-1.5 py-0.5 rounded">X-API-Key</code> header on every request. Generate keys in the <Link to="/app/developer" className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2">Developer Portal</Link>.
              </p>
              <CodeBlock code={`# All /v1/* endpoints\ncurl -H "X-API-Key: sk_your_api_key_here" \\\n     -H "Content-Type: application/json" \\\n     ${API_BASE}/v1/breach/search`} id="auth-example" />
              <div className="mt-4 p-4 bg-gray-900 border border-gray-800 rounded-lg">
                <p className="text-sm text-gray-400">
                  Every response includes a <code className="text-emerald-300 bg-gray-950 px-1 rounded text-xs">_credits</code> object:
                </p>
                <CodeBlock code={`"_credits": {\n  "credits_used": 3,\n  "credits_remaining": 47,\n  "credits_total": 50\n}`} id="credits-shape" />
              </div>

              <div className="mt-5">
                <h3 className="text-sm font-semibold text-gray-300 mb-3">Quick start</h3>
                <div className="flex gap-1 mb-3">
                  {(['curl', 'python', 'js'] as const).map(l => (
                    <button
                      key={l}
                      onClick={() => setQsLang(l)}
                      className={`px-3 py-1.5 text-xs rounded font-medium transition-colors ${qsLang === l ? 'bg-gray-700 text-gray-100' : 'text-gray-500 hover:text-gray-300'}`}
                    >
                      {l === 'js' ? 'JavaScript' : l.charAt(0).toUpperCase() + l.slice(1)}
                    </button>
                  ))}
                </div>
                <CodeBlock code={qsCode} id={`qs-${qsLang}`} />
              </div>
            </section>

            {/* Credit pricing */}
            <section id="pricing" className="mb-14 scroll-mt-20">
              <h2 className="text-xl font-bold text-white mb-4 pb-2 border-b border-gray-800">Credit Pricing</h2>
              <p className="text-sm text-gray-400 mb-4">Credits are deducted at call time. Insufficient credits return HTTP 402. Check balance via <code className="text-sm text-emerald-400 bg-gray-900 px-1.5 py-0.5 rounded">GET /developer/usage</code>.</p>
              <div className="border border-gray-800 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-900 border-b border-gray-800">
                      <th className="text-left px-4 py-3 text-xs text-gray-500 font-semibold uppercase tracking-wider">Module</th>
                      <th className="text-left px-4 py-3 text-xs text-gray-500 font-semibold uppercase tracking-wider">Endpoint</th>
                      <th className="text-center px-4 py-3 text-xs text-gray-500 font-semibold uppercase tracking-wider">Credits</th>
                      <th className="text-center px-4 py-3 text-xs text-gray-500 font-semibold uppercase tracking-wider">Min Plan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SECTIONS.flatMap(s => s.endpoints.filter(e => e.cost !== undefined && e.cost > 0).map(e => ({ ...e, module: s.title, plan: s.plan }))).map((row, i) => (
                      <tr key={i} className="border-b border-gray-800/60 last:border-0 hover:bg-gray-900/50 transition-colors">
                        <td className="px-4 py-2.5 text-gray-300 font-medium">{row.module}</td>
                        <td className="px-4 py-2.5 font-mono text-xs text-gray-400">{row.method} {row.path}</td>
                        <td className="px-4 py-2.5 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${COST_STYLE(row.cost!)}`}>{row.cost}</span>
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded border text-[11px] font-medium ${PLAN_STYLE[row.plan] || ''}`}>{row.plan}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* MCP */}
            <section id="mcp" className="mb-14 scroll-mt-20">
              <h2 className="text-xl font-bold text-white mb-4 pb-2 border-b border-gray-800">MCP Integration</h2>
              <p className="text-sm text-gray-400 leading-relaxed mb-5">
                Use Sycek OSINT directly from Claude Desktop, Cursor, or any MCP-compatible AI — no code required. Your API key is the credential. 20 tools across BreachINT, X/Twitter, and Social OSINT are exposed.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                <div className="border border-gray-800 rounded-xl p-5 bg-gray-900/40">
                  <div className="text-xs font-semibold text-gray-300 mb-1">Zero-Install (Hosted SSE)</div>
                  <p className="text-xs text-gray-500 mb-3">Point your AI client at the Sycek cloud endpoint. No Python required.</p>
                  <CodeBlock code={`{\n  "mcpServers": {\n    "sycek-osint": {\n      "type": "sse",\n      "url": "https://sycek.io/mcp/sse",\n      "headers": {\n        "Authorization": "Bearer sk_your_key_here"\n      }\n    }\n  }\n}`} id="mcp-sse" />
                  <p className="text-[11px] text-gray-600 mt-2">
                    Config: <code className="text-gray-500">%APPDATA%\Claude\claude_desktop_config.json</code>
                  </p>
                </div>

                <div className="border border-gray-800 rounded-xl p-5 bg-gray-900/40">
                  <div className="text-xs font-semibold text-gray-300 mb-1">Local Install (pip)</div>
                  <p className="text-xs text-gray-500 mb-3">Run the MCP server locally. Requires Python 3.10+.</p>
                  <CodeBlock code={`pip install sycek-osint-mcp\n\n# claude_desktop_config.json:\n{\n  "mcpServers": {\n    "sycek-osint": {\n      "command": "sycek-osint-mcp",\n      "env": {\n        "SYCEK_API_KEY": "sk_your_key_here"\n      }\n    }\n  }\n}`} id="mcp-pip" />
                </div>
              </div>

              <div className="border border-gray-800 rounded-xl p-5 bg-gray-900/30">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Available MCP Tools</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { label: 'BreachINT', tools: ['breach_search', 'breach_whois', 'breach_reverse_whois', 'breach_caller_id', 'breach_gmail_osint', 'breach_full_profile'] },
                    { label: 'X / Twitter', tools: ['twitter_user', 'twitter_tweets', 'twitter_search', 'twitter_replies', 'twitter_investigate', 'twitter_event', 'twitter_full_profile'] },
                    { label: 'Social Stream', tools: ['social_search', 'social_stream', 'social_analyze', 'social_intelligence', 'social_actor_profile', 'social_stix_export', 'social_investigate'] },
                  ].map(({ label, tools }) => (
                    <div key={label}>
                      <div className="text-xs font-semibold text-gray-300 mb-2">{label}</div>
                      <ul className="space-y-0.5">
                        {tools.map(t => (
                          <li key={t} className="text-xs text-gray-500 font-mono flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 shrink-0" />
                            {t}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* API sections */}
            {SECTIONS.map(section => {
              const accent = SECTION_ACCENT[section.id] || 'border-gray-600 text-gray-400'
              const [borderClass, textClass] = accent.split(' ')
              return (
                <section key={section.id} id={section.id} className="mb-14 scroll-mt-20">
                  <div className={`flex items-center gap-3 mb-2 pb-3 border-b ${borderClass}`}>
                    <h2 className={`text-xl font-bold text-white`}>{section.title}</h2>
                    <span className={`text-xs px-2 py-0.5 rounded border font-medium ${PLAN_STYLE[section.plan] || ''}`}>{section.plan}</span>
                    <span className="text-xs text-gray-600">{section.endpoints.length} endpoints</span>
                  </div>
                  <p className="text-sm text-gray-400 leading-relaxed mb-6">{section.description}</p>

                  {section.id !== 'developer' && (
                    <div className="mb-5 p-3 bg-gray-900 border border-gray-800 rounded-lg">
                      <span className="text-xs text-gray-500">Base path: </span>
                      <code className={`text-sm font-mono ${textClass}`}>{API_BASE}{section.basePrefix}</code>
                    </div>
                  )}

                  {section.endpoints.map((ep, idx) => (
                    <EndpointCard key={idx} ep={ep} sectionId={section.id} idx={idx} />
                  ))}
                </section>
              )
            })}

          </main>
        </div>
      </div>
    </>
  )
}
